import { create } from 'zustand'
import { useBetSliderStore } from './betSliderStore'
import { useConfigurationStore } from './configurationStore'
import { NavigationMessages, UltraPlayModalStateChangedMessage } from 'game-ui-types'

export const ULTRA_PLAY_SPIN_OPTIONS = [10, 20, 30, 40, 50, 70, 100, 500, 1000, -1]

// Unlimited engine autoplay unlocks the extended Ultra Play list. Otherwise keep the engine's list.
export const getUltraPlaySpinOptions = (autoplayOptions: number[]) =>
    autoplayOptions.includes(-1) ? ULTRA_PLAY_SPIN_OPTIONS : autoplayOptions

export const clampUltraPlaySpinsIndex = (index: number, options: number[]) =>
    Math.max(0, Math.min(index, options.length - 1))

export const getUltraPlayRemainingSpinsAfterSelection = (
    newCount: number,
    sessionStartingSpinNumber: number,
    currentSpinNumber: number,
    remainingSpins: number,
    options: number[]
) => {
    if (newCount === -1) return options.includes(-1) ? -1 : remainingSpins

    // Anchored to the session start so the scale doesn't drift and each session is capped at the top option.
    return Math.max(1, sessionStartingSpinNumber + newCount - currentSpinNumber)
}

// Special Buy Bonus dropdown option (games with Double Chance): behaves like a buy bonus feature in
// the dropdown but drives the engine's Double Chance toggle instead of a buy bonus purchase.
export const ULTRA_PLAY_DOUBLE_CHANCE = 'DOUBLE_CHANCE'
export const ULTRA_PLAY_BUY_FREESPINS = 'BUY_FREESPINS'

export type UltraPlayStatus = 'READY' | 'PLAYING' | 'PAUSED'
export type UltraPlaySpinType = 'NORMAL' | 'FREE_SPIN' | 'CASCADE' | 'BONUS'

export type UltraPlaySubSpin = {
    win: number
    winMultiplier: number
    balance: number
    type: UltraPlaySpinType
    freeSpinsAwarded?: number
}

export type UltraPlaySpin = {
    spinNumber: number
    bet: number
    win: number
    winMultiplier: number
    balance: number
    type: UltraPlaySpinType
    freeSpinsAwarded?: number
    subSpins?: UltraPlaySubSpin[]
    isExpanded: boolean
}

type UltraPlayStore = {
    isOpen: boolean
    status: UltraPlayStatus
    selectedBetIndex: number
    selectedSpinsIndex: number
    remainingSpins: number
    sessionStartingSpinNumber: number
    currentSpinNumber: number
    totalBet: number
    totalWin: number
    startingBalance: number
    lastBalance: number
    spins: UltraPlaySpin[]
    stopOnFeature: boolean
    selectedBuyBonusFeatureName: string
    showFeatureChoiceDialog: boolean
    featureDialogAutoplayable: boolean
    isInFeature: boolean
    isBuyBonusRoundInFlight: boolean
    previousSpeedLevel: number
    pendingAutoplayRestart: boolean
    showInsufficientBalance: boolean
    // Sticky: the game lost its connection (engine shows "no connection" in the info line and never
    // recovers without a reload). While set, Ultra Play blocks play. Not cleared by reset/open/close.
    isNoConnection: boolean
    isAwaitingAutoplayStop: boolean
    isBetInFlight: boolean
    isPausePending: boolean
    hasUnrecordedBet: boolean
    currentFeatureName: string

    open: () => void
    close: () => void
    setStatus: (status: UltraPlayStatus) => void
    setSelectedBetIndex: (index: number) => void
    setSelectedSpinsIndex: (index: number) => void
    setStopOnFeature: (stop: boolean) => void
    setSelectedBuyBonusFeatureName: (name: string) => void
    addSpin: (spin: Omit<UltraPlaySpin, 'isExpanded'>, expanded?: boolean) => void
    addFeatureMarkerSpin: (spin: Omit<UltraPlaySpin, 'isExpanded'>, expanded?: boolean) => void
    addSubSpin: (spinNumber: number, subSpin: UltraPlaySubSpin) => void
    addFeatureRetrigger: (parentSpinNumber: number, extraFreeSpins: number) => void
    updateSpin: (spinNumber: number, partial: Partial<UltraPlaySpin>) => void
    setSpinExpanded: (spinNumber: number, expanded: boolean) => void
    start: (startingBalance: number, remainingSpins: number) => void
    // Resume a finished session: keep the table, totals and spin counter; just add more spins.
    continueSession: (remainingSpins: number) => void
    pause: () => void
    resume: () => void
    stop: () => void
    setShowFeatureChoiceDialog: (show: boolean) => void
    setShowInsufficientBalance: (show: boolean) => void
    setNoConnection: (value: boolean) => void
    setFeatureDialogAutoplayable: (autoplayable: boolean) => void
    setIsInFeature: (inFeature: boolean) => void
    setBuyBonusRoundInFlight: (inFlight: boolean) => void
    toggleSpinExpanded: (spinNumber: number) => void
    decrementRemainingSpins: () => void
    extendRemainingSpins: (extraSpins: number) => void
    reset: () => void
    setPreviousSpeedLevel: (level: number) => void
    setPendingAutoplayRestart: (v: boolean) => void
    setAwaitingAutoplayStop: (v: boolean) => void
    setBetInFlight: (v: boolean) => void
    setPausePending: (v: boolean) => void
    setHasUnrecordedBet: (v: boolean) => void
    setCurrentFeatureName: (name: string) => void
}

export const useUltraPlayStore = create<UltraPlayStore>((set) => ({
    isOpen: false,
    status: 'READY',
    selectedBetIndex: 0,
    selectedSpinsIndex: 1,
    remainingSpins: 0,
    sessionStartingSpinNumber: 0,
    currentSpinNumber: 0,
    totalBet: 0,
    totalWin: 0,
    startingBalance: 0,
    lastBalance: 0,
    spins: [],
    stopOnFeature: false,
    selectedBuyBonusFeatureName: 'OFF',
    showFeatureChoiceDialog: false,
    featureDialogAutoplayable: false,
    isInFeature: false,
    isBuyBonusRoundInFlight: false,
    previousSpeedLevel: 0,
    pendingAutoplayRestart: false,
    showInsufficientBalance: false,
    isNoConnection: false,
    isAwaitingAutoplayStop: false,
    isBetInFlight: false,
    isPausePending: false,
    hasUnrecordedBet: false,
    currentFeatureName: '',

    // Reset both selections on open: bet from the live bet slider, spins to the default — so reopening
    // Ultra Play always starts from a clean state (previously spins kept the old selection).
    open: () => {
        const { activeIndex, getActiveBetOption } = useBetSliderStore.getState()

        set({
            isOpen: true,
            selectedBetIndex: getActiveBetOption()?.betIndex ?? activeIndex ?? 0,
            selectedSpinsIndex: clampUltraPlaySpinsIndex(
                1,
                getUltraPlaySpinOptions(useConfigurationStore.getState().autoplayOptions)
            ),
            currentFeatureName: '',
        })
    },
    close: () => set({ isOpen: false }),
    setStatus: (status) => set({ status }),
    setSelectedBetIndex: (index) => set({ selectedBetIndex: index }),
    setSelectedSpinsIndex: (index) => set({ selectedSpinsIndex: index }),
    setStopOnFeature: (stopOnFeature) => set({ stopOnFeature }),
    setSelectedBuyBonusFeatureName: (name) => set({ selectedBuyBonusFeatureName: name }),
    addSpin: (spin, expanded = false) =>
        set((state) => ({
            spins: [{ ...spin, isExpanded: expanded }, ...state.spins],
            currentSpinNumber: spin.spinNumber,
            totalBet: state.totalBet + spin.bet,
            totalWin: state.totalWin + spin.win,
            lastBalance: spin.balance,
        })),
    addFeatureMarkerSpin: (spin, expanded = false) =>
        set((state) => ({
            spins: [{ ...spin, isExpanded: expanded }, ...state.spins],
            totalBet: state.totalBet + spin.bet,
            totalWin: state.totalWin + spin.win,
            lastBalance: spin.balance,
        })),
    addSubSpin: (spinNumber, subSpin) =>
        set((state) => ({
            spins: state.spins.map((s) =>
                s.spinNumber === spinNumber ? { ...s, subSpins: [...(s.subSpins ?? []), subSpin] } : s
            ),
            totalWin: state.totalWin + subSpin.win,
            lastBalance: subSpin.balance,
        })),
    addFeatureRetrigger: (parentSpinNumber, extraFreeSpins) =>
        set((state) => ({
            spins: state.spins.map((spin) => {
                if (spin.spinNumber !== parentSpinNumber) return spin

                const subSpins = spin.subSpins

                if (subSpins && subSpins.length > 0) {
                    return {
                        ...spin,
                        subSpins: subSpins.map((subSpin, i) =>
                            i === subSpins.length - 1
                                ? { ...subSpin, freeSpinsAwarded: (subSpin.freeSpinsAwarded ?? 0) + extraFreeSpins }
                                : subSpin
                        ),
                    }
                }

                return spin
            }),
        })),
    updateSpin: (spinNumber, partial) =>
        set((state) => ({
            spins: state.spins.map((s) => (s.spinNumber === spinNumber ? { ...s, ...partial } : s)),
        })),
    setSpinExpanded: (spinNumber, expanded) =>
        set((state) => ({
            spins: state.spins.map((s) => (s.spinNumber === spinNumber ? { ...s, isExpanded: expanded } : s)),
        })),
    start: (startingBalance, remainingSpins) =>
        set({
            status: 'PLAYING',
            startingBalance,
            lastBalance: startingBalance,
            remainingSpins,
            sessionStartingSpinNumber: 0,
            currentSpinNumber: 0,
            totalBet: 0,
            totalWin: 0,
            spins: [],
            isInFeature: false,
            isBuyBonusRoundInFlight: false,
            showInsufficientBalance: false,
            isBetInFlight: false,
            isPausePending: false,
            isAwaitingAutoplayStop: false,
            hasUnrecordedBet: false,
        }),
    continueSession: (remainingSpins) =>
        set((state) => ({
            status: 'PLAYING',
            remainingSpins,
            sessionStartingSpinNumber: state.currentSpinNumber,
            isInFeature: false,
            isBuyBonusRoundInFlight: false,
            showInsufficientBalance: false,
            isBetInFlight: false,
            isPausePending: false,
            isAwaitingAutoplayStop: false,
        })),
    pause: () => set({ status: 'PAUSED', isBetInFlight: false, isPausePending: false, isBuyBonusRoundInFlight: false }),
    resume: () =>
        set({ status: 'PLAYING', isBetInFlight: false, isPausePending: false, isAwaitingAutoplayStop: false }),
    stop: () =>
        set({
            status: 'READY',
            remainingSpins: 0,
            isInFeature: false,
            isBuyBonusRoundInFlight: false,
            isAwaitingAutoplayStop: false,
            isBetInFlight: false,
            isPausePending: false,
        }),
    setShowFeatureChoiceDialog: (show) => set({ showFeatureChoiceDialog: show }),
    setShowInsufficientBalance: (show) => set({ showInsufficientBalance: show }),
    setNoConnection: (value) => set({ isNoConnection: value }),
    setFeatureDialogAutoplayable: (autoplayable) => set({ featureDialogAutoplayable: autoplayable }),
    setIsInFeature: (inFeature) => set({ isInFeature: inFeature }),
    setBuyBonusRoundInFlight: (inFlight) => set({ isBuyBonusRoundInFlight: inFlight }),
    toggleSpinExpanded: (spinNumber) =>
        set((state) => ({
            spins: state.spins.map((s) => (s.spinNumber === spinNumber ? { ...s, isExpanded: !s.isExpanded } : s)),
        })),
    decrementRemainingSpins: () =>
        // -1 marks infinite spins — keep it so the session never hits the "0 = target reached" stop.
        set((state) => ({
            remainingSpins: state.remainingSpins === -1 ? -1 : Math.max(0, state.remainingSpins - 1),
        })),
    extendRemainingSpins: (extraSpins) => set((state) => ({ remainingSpins: state.remainingSpins + extraSpins })),
    reset: () =>
        set({
            status: 'READY',
            remainingSpins: 0,
            sessionStartingSpinNumber: 0,
            currentSpinNumber: 0,
            totalBet: 0,
            totalWin: 0,
            lastBalance: 0,
            // Clear so the setup screen shows the live balance on reopen, not the previous session's start.
            startingBalance: 0,
            spins: [],
            showFeatureChoiceDialog: false,
            featureDialogAutoplayable: false,
            isInFeature: false,
            isBuyBonusRoundInFlight: false,
            selectedBuyBonusFeatureName: 'OFF',
            showInsufficientBalance: false,
            isAwaitingAutoplayStop: false,
            isBetInFlight: false,
            isPausePending: false,
            hasUnrecordedBet: false,
        }),
    setPreviousSpeedLevel: (level) => set({ previousSpeedLevel: level }),
    setPendingAutoplayRestart: (v) => set({ pendingAutoplayRestart: v }),
    setAwaitingAutoplayStop: (v) => set({ isAwaitingAutoplayStop: v }),
    setBetInFlight: (v) => set({ isBetInFlight: v }),
    setPausePending: (v) => set({ isPausePending: v }),
    setHasUnrecordedBet: (v) => set({ hasUnrecordedBet: v }),
    setCurrentFeatureName: (name) => set({ currentFeatureName: name }),
}))

useUltraPlayStore.subscribe((state, prevState) => {
    if (state.isOpen === prevState.isOpen) {
        return
    }

    const status = state.isOpen ? 'opened' : 'closed'
    const message: UltraPlayModalStateChangedMessage = {
        type: NavigationMessages.ULTRA_PLAY_MODAL_STATE_CHANGED,
        payload: { status },
    }

    window.uiBus.postMessage(message)
})
